import React, { useMemo, useState } from 'react';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { Download, FileText, X } from 'lucide-react';
import {
  NOMINAL_ACCOUNTS,
  assetRegisterRowsToCsv,
  balanceSheetRowsToCsv,
  buildAssetRegister,
  buildBalanceSheetFromTrialBalance,
  buildProfitAndLossFromTrialBalance,
  buildTaxComputationSummary,
  buildTrialBalance,
  profitAndLossRowsToCsv,
  taxComputationRowsToCsv,
  trialBalanceRowsToCsv
} from '../lib/accountingReports';

const formatMoney = (value, currencySymbol = 'EUR ') => {
  const amount = Number(value) || 0;
  return `${currencySymbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const downloadTextFile = (filename, content, mimeType = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const bankAccountsToCsv = (bankAccounts = []) => {
  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const rows = [
    ['Account Name', 'Type', 'Account Hint', 'Balance', 'As Of', 'Notes'],
    ...(Array.isArray(bankAccounts) ? bankAccounts : []).map((account) => [
      account.name || '',
      account.accountType || '',
      account.accountNumberHint || '',
      (Number(account.balance) || 0).toFixed(2),
      account.asOfDate || '',
      account.notes || ''
    ])
  ];
  return rows.map((row) => row.map(escapeCell).join(',')).join('\n');
};

const journalEntriesToCsv = (journalEntries = []) => {
  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const rows = [
    ['Journal ID', 'Date', 'Reference', 'Description', 'Account Code', 'Account Name', 'Type', 'Debit', 'Credit', 'Memo'],
    ...(Array.isArray(journalEntries) ? journalEntries : []).flatMap((journal) => (
      (Array.isArray(journal.lines) ? journal.lines : []).map((line) => [
        journal.id || '',
        journal.date || '',
        journal.reference || '',
        journal.description || '',
        line.accountCode || '',
        line.accountName || '',
        line.accountType || '',
        (Number(line.debit) || 0).toFixed(2),
        (Number(line.credit) || 0).toFixed(2),
        line.memo || ''
      ])
    ))
  ];
  return rows.map((row) => row.map(escapeCell).join(',')).join('\n');
};

const roundWorkbookNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const getReportDate = (item) => {
  const rawDate = item?.date || item?.purchaseDate || item?.invoiceDate || item?.createdAt;
  const parsed = rawDate ? new Date(rawDate) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const getReportYear = (item) => getReportDate(item)?.getFullYear() || null;

const getReportTransactions = (transactions = [], taxYear) => {
  const rows = Array.isArray(transactions) ? transactions : [];
  const selectedYear = Number(taxYear) || null;
  if (!selectedYear) return rows;
  return rows.filter((transaction) => getReportYear(transaction) === selectedYear);
};

const getReportBankAccounts = (bankAccounts = [], taxYear) => {
  const rows = Array.isArray(bankAccounts) ? bankAccounts : [];
  const selectedYear = Number(taxYear) || null;
  if (!selectedYear) return rows;
  const yearEndRows = rows.filter((account) => getReportYear({ date: account?.asOfDate }) === selectedYear);
  return yearEndRows.length > 0 ? yearEndRows : [];
};

const hasReportActivity = ({ transactions = [], bankAccounts = [], journalEntries = [], assetRegister } = {}) => (
  transactions.length > 0 ||
  bankAccounts.length > 0 ||
  journalEntries.length > 0 ||
  (assetRegister?.rows || []).length > 0
);

const buildPeriodReportBundle = ({
  transactions = [],
  assetTransactions = [],
  bankAccounts = [],
  journalEntries = [],
  taxYear,
  taxSummary = {},
  businessType,
  countryId,
  getTransactionVatAmount,
  isCapitalExpense
} = {}) => {
  const periodBankAccounts = getReportBankAccounts(bankAccounts, taxYear);
  const periodTransactions = getReportTransactions(transactions, taxYear);
  const periodJournalEntries = getReportTransactions(journalEntries, taxYear);
  const trialBalance = buildTrialBalance(transactions, {
    taxYear,
    getTransactionVatAmount,
    isCapitalExpense,
    journalEntries
  });
  const balanceSheet = buildBalanceSheetFromTrialBalance(trialBalance, {
    bankAccounts: periodBankAccounts
  });
  const profitAndLoss = buildProfitAndLossFromTrialBalance(trialBalance);
  const assetRegister = buildAssetRegister(assetTransactions, {
    taxYear,
    isCapitalExpense
  });
  const taxComputation = buildTaxComputationSummary({
    profitAndLoss,
    assetRegister,
    taxSummary,
    taxYear,
    businessStructure: taxSummary.businessStructure || '',
    businessType,
    countryId
  });

  return {
    taxYear,
    transactions: periodTransactions,
    bankAccounts: periodBankAccounts,
    journalEntries: periodJournalEntries,
    trialBalance,
    balanceSheet,
    profitAndLoss,
    assetRegister,
    taxComputation,
    hasData: hasReportActivity({
      transactions: periodTransactions,
      bankAccounts: periodBankAccounts,
      journalEntries: periodJournalEntries,
      assetRegister
    })
  };
};

const normalizeWorkbookProfile = (profile = {}) => {
  const source = profile && typeof profile === 'object' ? profile : {};
  const directorDetails = source.directorDetails && typeof source.directorDetails === 'object'
    ? source.directorDetails
    : {};
  return {
    businessName: source.businessName || '',
    tradingName: source.tradingName || '',
    businessAddress: source.businessAddress || '',
    companyRegistrationNumber: source.companyRegistrationNumber || '',
    incorporationDate: source.incorporationDate || '',
    taxReference: source.taxReference || '',
    businessType: source.businessType || '',
    industryId: source.industryId || '',
    directorDetails: {
      name: directorDetails.name || '',
      address: directorDetails.address || '',
      role: directorDetails.role || '',
      appointedDate: directorDetails.appointedDate || ''
    }
  };
};

const getProfileName = (businessProfile = {}) => {
  const profile = normalizeWorkbookProfile(businessProfile);
  return profile.businessName || profile.tradingName || 'Business name not entered';
};

const appendSheet = (workbook, sheetName, rows, columnWidths = []) => {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  if (columnWidths.length > 0) {
    worksheet['!cols'] = columnWidths.map((width) => ({ wch: width }));
  }
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
};

const appendReviewChecklistSheet = (workbook, {
  taxYear,
  trialBalance,
  balanceSheet,
  assetRegister,
  bankAccounts,
  journalEntries,
  sourceTransactions
}) => {
  const journalRows = Array.isArray(journalEntries) ? journalEntries : [];
  const bankRows = Array.isArray(bankAccounts) ? bankAccounts : [];
  const sourceRows = Array.isArray(sourceTransactions) ? sourceTransactions : [];
  const unbalancedJournalCount = journalRows.filter((journal) => !getJournalTotals(journal).isBalanced).length;
  const hasAssets = (assetRegister?.rows || []).length > 0;

  appendSheet(workbook, 'Review Checklist', [
    ['Review Area', 'Status', 'Notes'],
    ['Period selected', taxYear ? 'Ready' : 'Review', taxYear ? `Tax year ${taxYear}` : 'Workbook includes all available transactions.'],
    ['Source transactions', sourceRows.length > 0 ? 'Ready' : 'Review', `${sourceRows.length} transaction rows included.`],
    ['Trial balance', Math.abs(Number(trialBalance?.difference) || 0) < 0.01 ? 'Ready' : 'Review', `Difference: ${roundWorkbookNumber(trialBalance?.difference)}`],
    ['Balance sheet', Math.abs(Number(balanceSheet?.totals?.difference) || 0) < 0.01 ? 'Ready' : 'Review', `Difference: ${roundWorkbookNumber(balanceSheet?.totals?.difference)}`],
    ['Bank balances', bankRows.length > 0 ? 'Ready' : 'Review', bankRows.length > 0 ? `${bankRows.length} manual bank account balance rows included.` : 'Add cash/current/savings/card balances before finalising accounts.'],
    ['Fixed assets', hasAssets ? 'Ready' : 'Review', hasAssets ? `${assetRegister.rows.length} asset register rows included.` : 'No assets found for the selected period.'],
    ['Manual journals', unbalancedJournalCount === 0 ? 'Ready' : 'Review', unbalancedJournalCount === 0 ? `${journalRows.length} journals included.` : `${unbalancedJournalCount} unbalanced journal(s) excluded from reports.`],
    ['Accounts production', 'Manual review', 'Review classifications, opening balances, debtors, creditors, accruals, prepayments, stock, payroll, tax, and statutory disclosures before filing.']
  ], [24, 16, 78]);
};

const writeWorkbookFile = (filename, workbook) => {
  const workbookBytes = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([workbookBytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatDocumentMoney = (value) => {
  const amount = Number(value) || 0;
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const escapeXml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const getTextLines = (value) => String(value ?? '').split(/\r?\n/);

const docRun = (text = '', options = {}) => {
  const bold = options.bold ? '<w:b/>' : '';
  const italic = options.italic ? '<w:i/>' : '';
  const size = options.size ? `<w:sz w:val="${options.size}"/>` : '';
  const breaks = getTextLines(text);
  const content = breaks.map((line, index) => (
    `${index > 0 ? '<w:br/>' : ''}<w:t xml:space="preserve">${escapeXml(line)}</w:t>`
  )).join('');
  return `<w:r><w:rPr>${bold}${italic}${size}</w:rPr>${content}</w:r>`;
};

const docParagraph = (text = '', options = {}) => {
  const style = options.style ? `<w:pStyle w:val="${options.style}"/>` : '';
  const justification = options.align ? `<w:jc w:val="${options.align}"/>` : '';
  const spacing = `<w:spacing w:before="${options.before ?? 0}" w:after="${options.after ?? 160}" w:line="276" w:lineRule="auto"/>`;
  const keepNext = options.keepNext ? '<w:keepNext/>' : '';
  const pageBreakBefore = options.pageBreakBefore ? '<w:pageBreakBefore/>' : '';
  return `<w:p><w:pPr>${style}${justification}${spacing}${keepNext}${pageBreakBefore}</w:pPr>${docRun(text, options)}</w:p>`;
};

const docPageBreak = () => '<w:p><w:r><w:br w:type="page"/></w:r></w:p>';

const docTable = (rows = [], columnWidths = [], options = {}) => {
  const widths = columnWidths.length ? columnWidths : rows[0]?.map(() => 2400) || [9000];
  const grid = widths.map((width) => `<w:gridCol w:w="${width}"/>`).join('');
  const tableRows = rows.map((row, rowIndex) => {
    const cells = row.map((cell, cellIndex) => {
      const value = cell && typeof cell === 'object' && !Array.isArray(cell) ? cell : { text: cell };
      const width = widths[cellIndex] || widths[widths.length - 1] || 2400;
      const shading = options.shadeHeader !== false && (rowIndex === 0 || value.shade) ? '<w:shd w:fill="F3F4F6"/>' : '';
      const align = value.align || (typeof value.text === 'number' ? 'right' : '');
      return `<w:tc><w:tcPr><w:tcW w:w="${width}" w:type="dxa"/>${shading}<w:vAlign w:val="center"/></w:tcPr>${docParagraph(value.text ?? '', {
        bold: rowIndex === 0 || value.bold,
        align,
        after: 0
      })}</w:tc>`;
    }).join('');
    return `<w:tr>${cells}</w:tr>`;
  }).join('');
  const borders = options.borders === false
    ? '<w:tblBorders><w:top w:val="nil"/><w:left w:val="nil"/><w:bottom w:val="nil"/><w:right w:val="nil"/><w:insideH w:val="nil"/><w:insideV w:val="nil"/></w:tblBorders>'
    : '<w:tblBorders><w:top w:val="single" w:sz="4" w:color="D1D5DB"/><w:left w:val="single" w:sz="4" w:color="D1D5DB"/><w:bottom w:val="single" w:sz="4" w:color="D1D5DB"/><w:right w:val="single" w:sz="4" w:color="D1D5DB"/><w:insideH w:val="single" w:sz="4" w:color="D1D5DB"/><w:insideV w:val="single" w:sz="4" w:color="D1D5DB"/></w:tblBorders>';

  return `<w:tbl><w:tblPr><w:tblW w:w="${widths.reduce((sum, width) => sum + width, 0)}" w:type="dxa"/>${borders}<w:tblCellMar><w:top w:w="80" w:type="dxa"/><w:left w:w="80" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar></w:tblPr><w:tblGrid>${grid}</w:tblGrid>${tableRows}</w:tbl>`;
};

const buildDocxXml = (bodyXml = '') => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    ${bodyXml}
    <w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:header="708" w:footer="708" w:gutter="0"/></w:sectPr>
  </w:body>
</w:document>`;

const docxStylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:sz w:val="22"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:qFormat/><w:pPr><w:spacing w:after="240"/><w:jc w:val="center"/></w:pPr><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="32"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="260" w:after="120"/></w:pPr><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="28"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading2"><w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="200" w:after="80"/></w:pPr><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="24"/></w:rPr></w:style>
</w:styles>`;

const writeDocxFile = async (filename, bodyXml) => {
  const zip = new JSZip();
  zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/><Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/></Types>`);
  zip.folder('_rels').file('.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`);
  const word = zip.folder('word');
  word.file('document.xml', buildDocxXml(bodyXml));
  word.file('styles.xml', docxStylesXml);
  word.file('settings.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:zoom w:percent="100"/></w:settings>`);
  word.folder('_rels').file('document.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`);
  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildAccountsWorkingPaperWorkbook = ({
  taxYear,
  trialBalance,
  profitAndLoss,
  balanceSheet,
  taxComputation,
  assetRegister,
  bankAccounts,
  journalEntries,
  sourceTransactions,
  getTransactionVatAmount
} = {}) => {
  const workbook = XLSX.utils.book_new();
  const journalRows = Array.isArray(journalEntries) ? journalEntries : [];
  const sourceRows = Array.isArray(sourceTransactions) ? sourceTransactions : [];
  const ledgerRows = Array.isArray(trialBalance?.ledgerEntries) ? trialBalance.ledgerEntries : [];
  const unbalancedJournalCount = journalRows.filter((journal) => !getJournalTotals(journal).isBalanced).length;

  appendSheet(workbook, 'Summary', [
    ['Accounts Working Paper Pack'],
    ['Tax Year', taxYear || 'All time'],
    ['Source Transactions', sourceRows.length],
    ['Nominal Ledger Lines', ledgerRows.length],
    ['Trial Balance Difference', trialBalance?.difference ?? 0],
    ['Balance Sheet Difference', balanceSheet?.totals?.difference ?? 0],
    ['Tax Type', taxComputation?.taxLabel || ''],
    ['Tax Adjusted Profit', taxComputation?.totals?.taxableProfit ?? 0],
    ['Estimated Direct Tax', taxComputation?.totals?.totalTaxLiability ?? 0],
    ['Manual Journals', journalRows.length],
    ['Unbalanced Journals Excluded From Reports', unbalancedJournalCount]
  ], [34, 18]);

  appendReviewChecklistSheet(workbook, {
    taxYear,
    trialBalance,
    balanceSheet,
    assetRegister,
    bankAccounts,
    journalEntries,
    sourceTransactions: sourceRows
  });

  appendSheet(workbook, 'Source Transactions', [
    ['Date', 'Type', 'Category', 'Name', 'Description', 'Gross Amount', 'VAT Amount', 'Net Amount', 'Payment Method', 'Reference', 'Transaction ID'],
    ...sourceRows.map((transaction) => {
      const grossAmount = roundWorkbookNumber(transaction?.amount);
      const vatAmount = typeof getTransactionVatAmount === 'function'
        ? roundWorkbookNumber(getTransactionVatAmount(transaction))
        : roundWorkbookNumber(transaction?.vatAmount);
      const netAmount = roundWorkbookNumber(Math.abs(grossAmount) - Math.max(0, vatAmount));
      const counterparty = transaction?.clientName || transaction?.supplierName || transaction?.vendor || '';
      const reference = transaction?.invoiceNumber || transaction?.receiptNumber || transaction?.reference || transaction?.jobReference || '';
      return [
        transaction?.date || transaction?.purchaseDate || transaction?.invoiceDate || '',
        transaction?.type || '',
        transaction?.category || '',
        counterparty,
        transaction?.description || '',
        grossAmount,
        vatAmount,
        netAmount,
        transaction?.paymentMethod || '',
        reference,
        transaction?.id || ''
      ];
    })
  ], [14, 12, 22, 26, 36, 14, 14, 14, 16, 22, 24]);

  appendSheet(workbook, 'Nominal Ledger', [
    ['Date', 'Transaction ID', 'Description', 'Category', 'Account Code', 'Account Name', 'Account Type', 'Debit', 'Credit', 'Memo'],
    ...ledgerRows.map((entry) => [
      entry.date || '',
      entry.transactionId || '',
      entry.description || '',
      entry.category || '',
      entry.accountCode || '',
      entry.accountName || '',
      entry.accountType || '',
      entry.debit || 0,
      entry.credit || 0,
      entry.memo || ''
    ])
  ], [14, 24, 36, 22, 14, 34, 14, 14, 14, 28]);

  appendSheet(workbook, 'Trial Balance', [
    ['Account Code', 'Account Name', 'Type', 'Debit', 'Credit'],
    ...(trialBalance?.rows || []).map((row) => [
      row.accountCode,
      row.accountName,
      row.accountType,
      row.debit,
      row.credit
    ]),
    ['Total', '', '', trialBalance?.totalDebits || 0, trialBalance?.totalCredits || 0],
    ['Difference', '', '', trialBalance?.difference || 0, '']
  ], [14, 34, 14, 14, 14]);

  appendSheet(workbook, 'Profit and Loss', [
    ['Section', 'Account Code', 'Account Name', 'Amount'],
    ...(profitAndLoss?.sections?.income || []).map((row) => ['Income', row.accountCode, row.accountName, row.amount]),
    ['Total Income', '', '', profitAndLoss?.totals?.income || 0],
    [],
    ...(profitAndLoss?.sections?.expenses || []).map((row) => ['Expenses', row.accountCode, row.accountName, row.amount]),
    ['Total Expenses', '', '', profitAndLoss?.totals?.expenses || 0],
    ['Net Profit', '', '', profitAndLoss?.totals?.netProfit || 0]
  ], [18, 14, 34, 16]);

  const balanceSheetRows = [['Section', 'Account Code', 'Account Name', 'Amount']];
  Object.entries({
    nonCurrentAssets: 'Non-Current Assets',
    currentAssets: 'Current Assets',
    currentLiabilities: 'Current Liabilities',
    nonCurrentLiabilities: 'Non-Current Liabilities',
    equity: 'Capital and Reserves'
  }).forEach(([sectionKey, sectionLabel]) => {
    (balanceSheet?.sections?.[sectionKey] || []).forEach((row) => {
      balanceSheetRows.push([sectionLabel, row.accountCode, row.accountName, row.amount]);
    });
  });
  balanceSheetRows.push(
    ['Total Assets', '', '', balanceSheet?.totals?.assets || 0],
    ['Total Liabilities', '', '', balanceSheet?.totals?.liabilities || 0],
    ['Total Capital and Reserves', '', '', balanceSheet?.totals?.equity || 0],
    ['Total Liabilities and Capital', '', '', balanceSheet?.totals?.liabilitiesAndEquity || 0],
    ['Check Difference', '', '', balanceSheet?.totals?.difference || 0]
  );
  appendSheet(workbook, 'Balance Sheet', balanceSheetRows, [26, 14, 34, 16]);

  appendSheet(workbook, 'Tax Computation', [
    ['Section', 'Label', 'Amount'],
    ['Summary', 'Tax Year', taxComputation?.taxYear || taxYear || ''],
    ['Summary', 'Business Structure', taxComputation?.businessStructure || ''],
    ['Summary', 'Tax Type', taxComputation?.taxLabel || ''],
    ...(taxComputation?.rows || []).map((row) => ['Tax Computation', row.label, row.amount]),
    ...(taxComputation?.capitalAllowanceRows || [])
      .filter((row) => (Number(row.bookDepreciation) || 0) !== 0 || (Number(row.capitalAllowance) || 0) !== 0)
      .map((row) => ['Capital Allowances', row.description || row.id || '', Number(row.capitalAllowance) || 0])
  ], [24, 42, 16]);

  appendSheet(workbook, 'Asset Register', [
    ['Asset', 'Category', 'Purchase Date', 'Cost', 'Depn Years', 'Annual Depn', 'Accum Depn', 'NBV', 'Status', 'Disposal Date', 'Disposal Proceeds', 'Gain / Loss', 'Capital Allowance'],
    ...(assetRegister?.rows || []).map((asset) => [
      asset.description,
      asset.category,
      asset.purchaseDate,
      asset.cost,
      asset.depreciationYears,
      asset.annualDepreciation,
      asset.accumulatedDepreciation,
      asset.netBookValue,
      asset.status,
      asset.disposalDate,
      asset.disposalAmount,
      asset.gainOrLossOnDisposal,
      asset.capitalAllowanceForYear
    ])
  ], [28, 18, 14, 14, 12, 14, 14, 14, 14, 14, 18, 14, 18]);

  appendSheet(workbook, 'Banking', [
    ['Account Name', 'Type', 'Account Hint', 'Balance', 'As Of', 'Notes'],
    ...(Array.isArray(bankAccounts) ? bankAccounts : []).map((account) => [
      account.name || '',
      account.accountType || '',
      account.accountNumberHint || '',
      Number(account.balance) || 0,
      account.asOfDate || '',
      account.notes || ''
    ])
  ], [28, 16, 18, 14, 14, 34]);

  appendSheet(workbook, 'Manual Journals', [
    ['Journal ID', 'Date', 'Reference', 'Description', 'Status', 'Difference', 'Account Code', 'Account Name', 'Type', 'Debit', 'Credit', 'Memo'],
    ...journalRows.flatMap((journal) => {
      const totals = getJournalTotals(journal);
      const lines = Array.isArray(journal.lines) ? journal.lines : [];
      if (lines.length === 0) {
        return [[journal.id || '', journal.date || '', journal.reference || '', journal.description || '', totals.isBalanced ? 'Balanced' : 'Excluded until balanced', totals.difference, '', '', '', '', '', '']];
      }
      return lines.map((line) => [
        journal.id || '',
        journal.date || '',
        journal.reference || '',
        journal.description || '',
        totals.isBalanced ? 'Balanced' : 'Excluded until balanced',
        totals.difference,
        line.accountCode || '',
        line.accountName || '',
        line.accountType || '',
        Number(line.debit) || 0,
        Number(line.credit) || 0,
        line.memo || ''
      ]);
    })
  ], [24, 14, 14, 28, 24, 14, 14, 34, 14, 14, 14, 28]);

  return workbook;
};

const getBalanceSheetSectionRows = (balanceSheet = {}) => {
  const rows = [['Section', 'Account Code', 'Account Name', 'Amount']];
  Object.entries({
    nonCurrentAssets: 'Fixed assets',
    currentAssets: 'Current assets',
    currentLiabilities: 'Creditors: amounts falling due within one year',
    nonCurrentLiabilities: 'Creditors: amounts falling due after more than one year',
    equity: 'Capital and reserves'
  }).forEach(([sectionKey, sectionLabel]) => {
    (balanceSheet?.sections?.[sectionKey] || []).forEach((row) => {
      rows.push([sectionLabel, row.accountCode, row.accountName, row.amount]);
    });
  });
  return rows;
};

const getStatementOfFinancialPositionFigures = ({ balanceSheet, profitAndLoss, taxComputation, assetRegister } = {}) => {
  const hasAssetRegisterValues = (assetRegister?.rows || []).length > 0;
  const fixedAssets = hasAssetRegisterValues
    ? assetRegister?.totals?.netBookValue || 0
    : balanceSheet?.totals?.nonCurrentAssets || 0;
  const currentAssets = balanceSheet?.totals?.currentAssets || 0;
  const directTaxLiability = taxComputation?.totals?.totalTaxLiability || 0;
  const currentLiabilities = (balanceSheet?.totals?.currentLiabilities || 0) + directTaxLiability;
  const nonCurrentLiabilities = balanceSheet?.totals?.nonCurrentLiabilities || 0;
  const netCurrentAssets = currentAssets - currentLiabilities;
  const totalAssetsLessCurrentLiabilities = fixedAssets + netCurrentAssets;
  const netAssets = totalAssetsLessCurrentLiabilities - nonCurrentLiabilities;
  const capitalAndReserves = netAssets;
  const accountingProfit = taxComputation?.totals?.accountingProfit ?? profitAndLoss?.totals?.netProfit ?? 0;
  const profitAfterTax = accountingProfit - directTaxLiability;

  return {
    fixedAssets,
    currentAssets,
    currentLiabilities,
    nonCurrentLiabilities,
    netCurrentAssets,
    totalAssetsLessCurrentLiabilities,
    netAssets,
    capitalAndReserves,
    profitAfterTax,
    reservesBroughtForward: capitalAndReserves - profitAfterTax
  };
};

const buildAbridgedAccountsDraftDocument = ({
  templateType,
  businessProfile,
  taxYear,
  profitAndLoss,
  balanceSheet,
  taxComputation,
  assetRegister,
  bankAccounts,
  comparativePeriod
} = {}) => {
  const profile = normalizeWorkbookProfile(businessProfile);
  const businessName = getProfileName(profile);
  const templateLabel = templateType === 'frs102_1a'
    ? 'FRS 102 Section 1A Unaudited Financial Statements'
    : 'Abridged Unaudited Financial Statements';
  const isLimitedCompany = profile.businessType === 'limited_company';
  const currentFigures = getStatementOfFinancialPositionFigures({ balanceSheet, profitAndLoss, taxComputation, assetRegister });
  const comparativeFigures = comparativePeriod?.hasData
    ? getStatementOfFinancialPositionFigures({
      balanceSheet: comparativePeriod.balanceSheet,
      profitAndLoss: comparativePeriod.profitAndLoss,
      taxComputation: comparativePeriod.taxComputation,
      assetRegister: comparativePeriod.assetRegister
    })
    : null;
  const profitAfterTax = currentFigures.profitAfterTax;
  const bankTotal = (Array.isArray(bankAccounts) ? bankAccounts : [])
    .reduce((sum, account) => sum + (Number(account.balance) || 0), 0);
  const balanceRows = getBalanceSheetSectionRows(balanceSheet).slice(1);
  const yearLabel = taxYear ? String(taxYear).slice(-2) : 'YY';
  const periodEndLabel = taxYear ? `31 December ${taxYear}` : '[period end]';
  const periodFullLabel = taxYear ? `1 January ${taxYear} to 31 December ${taxYear}` : '[period]';
  const fixedAssets = currentFigures.fixedAssets;
  const currentAssets = currentFigures.currentAssets;
  const currentLiabilities = currentFigures.currentLiabilities;
  const netCurrentAssets = currentFigures.netCurrentAssets;
  const totalAssetsLessCurrentLiabilities = currentFigures.totalAssetsLessCurrentLiabilities;
  const netAssets = currentFigures.netAssets;
  const capitalAndReserves = currentFigures.capitalAndReserves;
  const comparativeYearLabel = comparativePeriod?.taxYear ? String(comparativePeriod.taxYear).slice(-2) : '';
  const hasComparatives = Boolean(comparativeFigures);
  const frs105AmountHeader = hasComparatives
    ? ['', { text: `Dec ${yearLabel}`, align: 'right', bold: true }, { text: `Dec ${comparativeYearLabel}`, align: 'right', bold: true }]
    : ['', { text: `Dec ${yearLabel}`, align: 'right', bold: true }];
  const frs105CurrencyHeader = hasComparatives
    ? ['', { text: 'EUR', align: 'right', bold: true }, { text: 'EUR', align: 'right', bold: true }]
    : ['', { text: 'EUR', align: 'right', bold: true }];
  const frs105Row = (label, currentValue, comparativeValue, options = {}) => (
    hasComparatives
      ? [
        options.bold ? { text: label, bold: true } : label,
        { text: options.bracketCurrent ? `(${formatDocumentMoney(currentValue)})` : formatDocumentMoney(currentValue), align: 'right', bold: Boolean(options.bold) },
        { text: options.bracketComparative ? `(${formatDocumentMoney(comparativeValue)})` : formatDocumentMoney(comparativeValue), align: 'right', bold: Boolean(options.bold) }
      ]
      : [
        options.bold ? { text: label, bold: true } : label,
        { text: options.bracketCurrent ? `(${formatDocumentMoney(currentValue)})` : formatDocumentMoney(currentValue), align: 'right', bold: Boolean(options.bold) }
      ]
  );
  const frs105Widths = hasComparatives ? [4700, 1700, 1700] : [6000, 2100];
  const balanceTableRows = [
    ['Section', 'Code', 'Account', { text: 'Amount', align: 'right' }],
    ...(balanceRows.length ? balanceRows.map((row) => [
      row[0],
      row[1],
      row[2],
      { text: formatDocumentMoney(row[3]), align: 'right' }
    ]) : [['No balance sheet rows generated.', '', '', '']]),
    [{ text: 'Total assets', bold: true }, '', '', { text: formatDocumentMoney(balanceSheet?.totals?.assets || 0), bold: true, align: 'right' }],
    [{ text: 'Total liabilities', bold: true }, '', '', { text: formatDocumentMoney(balanceSheet?.totals?.liabilities || 0), bold: true, align: 'right' }],
    [{ text: 'Capital and reserves', bold: true }, '', '', { text: formatDocumentMoney(balanceSheet?.totals?.equity || 0), bold: true, align: 'right' }]
  ];
  const cover = templateType === 'frs105'
    ? [
      docTable([
        ['', { text: `Company Number: ${isLimitedCompany ? profile.companyRegistrationNumber || '[company number]' : 'Not applicable'}`, align: 'right', bold: true }]
      ], [4200, 3900], { borders: false, shadeHeader: false }),
      docParagraph('', { after: 980 }),
      docParagraph(businessName, { align: 'center', bold: true, size: 28, after: 260 }),
      docParagraph(templateLabel, { align: 'center', bold: true, size: 28, after: 260 }),
      docParagraph(`for the financial period from ${periodFullLabel}`, { align: 'center', bold: true, size: 24, after: 720 })
    ].join('')
    : [
      docParagraph(`Registered number: ${isLimitedCompany ? profile.companyRegistrationNumber || '[company number]' : 'Not applicable'}`, { after: 520 }),
      docParagraph(businessName, { style: 'Title', after: 200 }),
      docParagraph(templateLabel, { align: 'center', bold: true, size: 26 }),
      docParagraph(`for the financial year ended ${periodEndLabel}`, { align: 'center', after: 520 }),
      docParagraph('Draft prepared from 70Lives ledger data. Review, amend, approve, and export to PDF before signing or filing.', { align: 'center', italic: true })
    ].join('');

  if (templateType === 'frs105') {
    return [
      cover,
      docPageBreak(),
      docParagraph(businessName, { bold: true, size: 24, after: 520 }),
      docParagraph('CONTENTS', { bold: true, size: 30, after: 620 }),
      docTable([
        ['', { text: 'Page', align: 'right', bold: true }],
        ['Statement of Financial Position', { text: '3', align: 'right' }],
        ['Notes to the Financial Statements', { text: '4 - 5', align: 'right' }]
      ], [6200, 1900], { borders: false, shadeHeader: false }),
      docPageBreak(),
      docParagraph(businessName, { bold: true, size: 24, after: 0 }),
      docParagraph('STATEMENT OF FINANCIAL POSITION', { bold: true, size: 32, after: 0 }),
      docParagraph(`as at ${periodEndLabel}`, { bold: true, size: 22, after: 540 }),
      docTable([
        frs105AmountHeader,
        frs105CurrencyHeader,
        frs105Row('Fixed Assets', fixedAssets, comparativeFigures?.fixedAssets),
        hasComparatives ? ['', '', ''] : ['', ''],
        frs105Row('Current assets', currentAssets, comparativeFigures?.currentAssets),
        frs105Row('Creditors: amounts falling due within one year', currentLiabilities, comparativeFigures?.currentLiabilities, {
          bracketCurrent: true,
          bracketComparative: true
        }),
        frs105Row('Net Current Assets', netCurrentAssets, comparativeFigures?.netCurrentAssets, { bold: true }),
        hasComparatives ? ['', '', ''] : ['', ''],
        frs105Row('Total Assets less Current Liabilities', totalAssetsLessCurrentLiabilities, comparativeFigures?.totalAssetsLessCurrentLiabilities, { bold: true }),
        ...(
          currentFigures.nonCurrentLiabilities || comparativeFigures?.nonCurrentLiabilities
            ? [frs105Row('Creditors: amounts falling due after more than one year', currentFigures.nonCurrentLiabilities, comparativeFigures?.nonCurrentLiabilities, {
              bracketCurrent: true,
              bracketComparative: true
            })]
            : []
        ),
        frs105Row('Net Assets', netAssets, comparativeFigures?.netAssets, { bold: true }),
        hasComparatives ? ['', '', ''] : ['', ''],
        frs105Row('Capital and Reserves', capitalAndReserves, comparativeFigures?.capitalAndReserves, { bold: true })
      ], frs105Widths, { borders: false, shadeHeader: false }),
      docParagraph('', { after: 260 }),
      docParagraph('The financial statements have been prepared in accordance with the micro-companies regime and FRS 105 "The Financial Reporting Standard applicable to the Micro-Entities Regime".'),
      docParagraph(`We as Directors of ${businessName}, state that -`, { after: 100 }),
      docParagraph('(a) the company is availing itself of the exemption provided for by Chapter 15 of Part 6 of the Companies Act 2014,', { after: 80 }),
      docParagraph('(b) the company is availing itself of the exemption on the grounds that the conditions specified in section 358 are satisfied,', { after: 80 }),
      docParagraph('(c) the shareholders of the company have not served a notice on the company under section 334(1) in accordance with section 334(2),', { after: 80 }),
      docParagraph('(d) we acknowledge the company obligations under the Companies Act 2014, to keep adequate accounting records and prepare financial statements which give a true and fair view of the assets, liabilities and financial position of the company at the end of its financial year and of its profit or loss for such a financial period and to otherwise comply with the provisions of the Companies Act 2014 relating to financial statements so far as they are applicable to the company,', { after: 80 }),
      docParagraph('(e) the company has relied on the specified exemption contained in section 352 Companies Act 2014 as a micro company. The company has done so on the grounds that the company is entitled to the benefit of that exemption as a small company and the abridged financial statements have been properly prepared in accordance with section 353 Companies Act 2014 and the micro companies regime.', { after: 260 }),
      docParagraph('Approved by the Directors and authorised for issue on ____________________ and signed on its behalf:', { bold: true, after: 260 }),
      docTable([
        ['______________________________', ''],
        [profile.directorDetails.name || '[director name]', 'Director']
      ], [4200, 3900], { borders: false, shadeHeader: false }),
      docPageBreak(),
      docParagraph(businessName, { bold: true, size: 24, after: 0 }),
      docParagraph('NOTES TO THE ABRIDGED FINANCIAL STATEMENTS', { bold: true, size: 30, after: 0 }),
      docParagraph(`for the financial period from ${periodFullLabel}`, { size: 20, after: 520 }),
      docParagraph('1.        General Information', { bold: true, size: 22 }),
      docParagraph(`${businessName} is a company limited by shares incorporated and registered in the Republic of Ireland. The registered number of the company is ${profile.companyRegistrationNumber || '[company number]'}. The registered office of the company is ${profile.businessAddress || '[registered office]'} which is also the principal place of business of the company. The financial statements have been presented in Euro (EUR) which is also the functional currency of the company. The company was incorporated on ${profile.incorporationDate || '[incorporation date]'}.`),
      docParagraph('2.        Summary of Significant Accounting Policies', { bold: true, size: 22 }),
      docParagraph('The following accounting policies have been applied consistently in dealing with items which are considered material in relation to the company financial statements.'),
      docParagraph('Basis of preparation', { bold: true, size: 22 }),
      docParagraph('The financial statements have been prepared on the going concern basis and in accordance with the historical cost convention. The financial reporting framework that has been applied in their preparation is the Companies Act 2014 and FRS 105 "The Financial Reporting Standard applicable to the Micro-Entities Regime" issued by the Financial Reporting Council.'),
      docParagraph('The company qualifies as a micro company as defined by section 280D of the Companies Act 2014 in respect of the financial period, and has applied the rules of the Micro Companies Regime in accordance with section 280E of the Companies Act 2014 and FRS 105.'),
      docParagraph('Turnover', { bold: true, size: 22 }),
      docParagraph('Turnover comprises the invoice value of goods and services supplied by the company, exclusive of trade discounts and value added tax.'),
      docParagraph('Tangible assets and depreciation', { bold: true, size: 22 }),
      docParagraph('Tangible assets are stated at cost or at valuation, less accumulated depreciation. The charge to depreciation is calculated to write off the original cost or valuation of tangible assets, less their estimated residual value, over their expected useful lives.'),
      docTable([
        ['Plant, machinery & Motor vehicles', '-', '20% Straight line'],
        ['Fixtures, fittings & office equipment', '-', '33% Straight line']
      ], [4200, 500, 2400], { borders: false, shadeHeader: false }),
      docParagraph('The carrying values of tangible fixed assets are reviewed annually for impairment in periods if events or changes in circumstances indicate the carrying value may not be recoverable.'),
      docParagraph('Trade and other debtors', { bold: true, size: 22 }),
      docParagraph('Trade and other debtors are initially recognised at fair value and thereafter stated at amortised cost using the effective interest method less impairment losses for bad and doubtful debts except where the effect of discounting would be immaterial. In such cases the receivables are stated at cost less impairment losses for bad and doubtful debts.'),
      docParagraph('Trade and other creditors', { bold: true, size: 22 }),
      docParagraph('Trade and other creditors are initially recognised at fair value and thereafter stated at amortised cost using the effective interest rate method, unless the effect of discounting would be immaterial, in which case they are stated at cost.'),
      docParagraph('Taxation', { bold: true, size: 22 }),
      docParagraph('Current tax represents the amount expected to be paid or recovered in respect of taxable profits for the financial period and is calculated using the tax rates and laws that have been enacted or substantively enacted at the Statement of Financial Position date.'),
      docPageBreak(),
      docParagraph(businessName, { bold: true, size: 24, after: 0 }),
      docParagraph('NOTES TO THE ABRIDGED FINANCIAL STATEMENTS', { bold: true, size: 30, after: 0 }),
      docParagraph(`continued for the financial period from ${periodFullLabel}`, { size: 20, after: 520 }),
      docParagraph('Foreign currencies', { bold: true, size: 22 }),
      docParagraph('Monetary assets and liabilities denominated in foreign currencies are translated at the rates of exchange ruling at the balance sheet date. Transactions during the financial period which are denominated in foreign currencies are translated at the rates of exchange ruling at the date of the transaction. The resulting exchange differences are dealt with in the profit and loss account.'),
      docParagraph('Share capital of the company', { bold: true, size: 22 }),
      docParagraph('Ordinary share capital', { bold: true, size: 22 }),
      docParagraph('The ordinary share capital of the company is presented as equity.'),
      docParagraph('Exceptional item', { bold: true, size: 22 }),
      docParagraph('Exceptional items are those that the directors view are required to be separately disclosed by virtue of their size or incidence to enable a full understanding of the company financial performance.'),
      docParagraph('3.        Appropriation of Income Statement', { bold: true, size: 22 }),
      docTable([
        frs105AmountHeader,
        frs105CurrencyHeader,
        frs105Row('Profit / (loss) brought forward', currentFigures.reservesBroughtForward, comparativeFigures?.reservesBroughtForward),
        frs105Row('Profit / (loss) for the financial period', profitAfterTax, comparativeFigures?.profitAfterTax),
        frs105Row('Profit / (loss) carried forward', capitalAndReserves, comparativeFigures?.capitalAndReserves, { bold: true })
      ], frs105Widths, { borders: false, shadeHeader: false }),
      docParagraph('4.        Post-Balance Sheet Events', { bold: true, size: 22 }),
      docParagraph('There have been no significant events affecting the company since the financial period-end.'),
      docParagraph('5.        Approval of financial statements', { bold: true, size: 22 }),
      docParagraph('The financial statements were approved and authorised for issue by the board of directors on'),
      docParagraph('______________________________', { after: 260 })
    ].join('');
  }

  return [
    cover,
    docPageBreak(),
    docParagraph(businessName, { style: 'Heading1' }),
    docParagraph('Contents', { style: 'Heading2' }),
    docTable([
      ['Section', 'Page'],
      ['Company Information', '1'],
      ['Directors Report', '2'],
      ['Directors Responsibilities Statement', '3'],
      ['Accountants Report', '4'],
      ['Profit and Loss Account', '5'],
      ['Balance Sheet', '6'],
      ['Statement of Changes in Equity', '7'],
      ['Notes to the Financial Statements', '8']
    ], [6500, 1600]),
    docPageBreak(),
    docParagraph('Company Information', { style: 'Heading1' }),
    docTable([
      ['Field', 'Details'],
      ['Director', profile.directorDetails.name || '[director name]'],
      ['Registered number', profile.companyRegistrationNumber || '[company number]'],
      ['Registered office', profile.businessAddress || '[registered office]'],
      ['Tax reference', profile.taxReference || '[tax reference]'],
      ['Accounting framework', 'FRS 102 Section 1A']
    ], [2500, 5600]),
    docPageBreak(),
    docParagraph(`Directors Report for the financial year ended ${taxYear || '[period end]'}`, { style: 'Heading1' }),
    docParagraph(`The director presents the annual report and unaudited financial statements for ${businessName} for the financial year ended ${taxYear || '[period end]'}.`),
    docParagraph('Principal activities', { style: 'Heading2' }),
    docParagraph('The principal activity of the company should be reviewed and amended by the accountant or director before approval.'),
    docParagraph('Results and dividends', { style: 'Heading2' }),
    docParagraph(`The profit / (loss) for the financial year after taxation amounted to ${formatDocumentMoney(profitAfterTax)}. No dividend is proposed unless amended by the directors.`),
    docParagraph('Accounting records', { style: 'Heading2' }),
    docParagraph(`The measures taken by the directors to secure compliance with the Companies Act 2014 include maintaining accounting records and supporting documentation. The records are maintained at ${profile.businessAddress || '[registered office]'}.`),
    docParagraph('Post balance sheet events', { style: 'Heading2' }),
    docParagraph('There have been no significant events subsequent to the financial year end that would require adjustment or disclosure unless amended by the preparer.'),
    docPageBreak(),
    docParagraph('Directors Responsibilities Statement', { style: 'Heading1' }),
    docParagraph('The directors are responsible for preparing the directors report and financial statements in accordance with Irish law and regulations. They must not approve the financial statements unless satisfied that they give a true and fair view and otherwise comply with the Companies Act 2014.'),
    docParagraph('In preparing these financial statements, the directors are required to select suitable accounting policies, apply them consistently, make reasonable and prudent judgments and estimates, and prepare the financial statements on the going concern basis unless inappropriate.'),
    docPageBreak(),
    docParagraph('Accountants Report', { style: 'Heading1' }),
    docParagraph('This section is a draft placeholder for the accountant compilation report. It should be replaced, amended, or removed by the accountant according to the terms of engagement and professional requirements. No audit opinion is expressed by this draft document.'),
    docPageBreak(),
    docParagraph(`Profit and Loss Account for the year ended ${taxYear || '[period end]'}`, { style: 'Heading1' }),
    docTable([
      ['Line', { text: 'Amount', align: 'right' }],
      ['Turnover', { text: formatDocumentMoney(profitAndLoss?.totals?.income || 0), align: 'right' }],
      ['Administrative expenses', { text: `(${formatDocumentMoney(profitAndLoss?.totals?.expenses || 0)})`, align: 'right' }],
      [{ text: 'Profit / (loss) before taxation', bold: true }, { text: formatDocumentMoney(profitAndLoss?.totals?.netProfit || 0), bold: true, align: 'right' }],
      [taxComputation?.taxLabel || 'Tax charge', { text: `(${formatDocumentMoney(taxComputation?.totals?.totalTaxLiability || 0)})`, align: 'right' }],
      [{ text: 'Profit / (loss) for the financial year', bold: true }, { text: formatDocumentMoney(profitAfterTax), bold: true, align: 'right' }]
    ], [6100, 2000]),
    docPageBreak(),
    docParagraph(`Balance Sheet as at ${taxYear || '[period end]'}`, { style: 'Heading1' }),
    docTable(balanceTableRows, [2500, 1200, 3000, 1600]),
    docParagraph('The company is availing itself of the small companies exemption under the Companies Act 2014 where applicable. The directors acknowledge the company obligations to keep adequate accounting records and prepare financial statements that give a true and fair view.'),
    docTable([
      ['Approved by', 'Signature / date'],
      [profile.directorDetails.name || '[director name]', '______________________________']
    ], [4200, 3900]),
    docPageBreak(),
    docParagraph('Statement of Changes in Equity', { style: 'Heading1' }),
    docTable([
      ['Line', { text: 'Share capital', align: 'right' }, { text: 'Profit and loss', align: 'right' }, { text: 'Total equity', align: 'right' }],
      ['Profit / (loss) for the year', { text: '-', align: 'right' }, { text: formatDocumentMoney(profitAfterTax), align: 'right' }, { text: formatDocumentMoney(profitAfterTax), align: 'right' }],
      ['At year end', { text: '[share capital]', align: 'right' }, { text: formatDocumentMoney(profitAfterTax), align: 'right' }, { text: formatDocumentMoney(balanceSheet?.totals?.equity || profitAfterTax), align: 'right' }]
    ], [2700, 1800, 1800, 1800]),
    docPageBreak(),
    docParagraph(`Notes to the Financial Statements for the year ended ${taxYear || '[period end]'}`, { style: 'Heading1' }),
    docParagraph('1. General Information', { style: 'Heading2' }),
    docParagraph(`${businessName} is a private company limited by shares, incorporated in Ireland with company registration number ${profile.companyRegistrationNumber || '[company number]'}. The registered office is ${profile.businessAddress || '[registered office]'}.`),
    docParagraph('2. Accounting Policies', { style: 'Heading2' }),
    docParagraph('The financial statements have been prepared under the historical cost convention and on the going concern basis in accordance with Section 1A of FRS 102 and the Companies Act 2014. The functional and presentation currency is Euro unless amended by the preparer.'),
    docParagraph('3. Fixed Assets, Cash and Tax', { style: 'Heading2' }),
    docTable([
      ['Line', { text: 'Amount', align: 'right' }],
      ['Fixed assets cost', { text: formatDocumentMoney(assetRegister?.totals?.cost || 0), align: 'right' }],
      ['Accumulated depreciation', { text: formatDocumentMoney(assetRegister?.totals?.accumulatedDepreciation || 0), align: 'right' }],
      ['Net book value', { text: formatDocumentMoney(assetRegister?.totals?.netBookValue || 0), align: 'right' }],
      ['Bank balances', { text: formatDocumentMoney(bankTotal), align: 'right' }],
      ['Capital allowances', { text: formatDocumentMoney(taxComputation?.totals?.capitalAllowances || 0), align: 'right' }]
    ], [6100, 2000]),
    docParagraph('4. Post Balance Sheet Events', { style: 'Heading2' }),
    docParagraph('There have been no significant events subsequent to the financial year end that would require adjustment or disclosure unless amended by the preparer.'),
    docParagraph('5. Approval of Financial Statements', { style: 'Heading2' }),
    docParagraph('The board of directors approved these financial statements for issue on ____________________.')
  ].join('');
};

const buildTaxReturnDraftWorkbook = ({
  businessProfile,
  taxYear,
  businessType,
  profitAndLoss,
  taxComputation,
  assetRegister
} = {}) => {
  const workbook = XLSX.utils.book_new();
  const profile = normalizeWorkbookProfile({ ...businessProfile, businessType });
  const isLimitedCompany = businessType === 'limited_company';
  const returnLabel = isLimitedCompany ? 'Corporation Tax Return Draft' : 'Income Tax Return Draft';

  appendSheet(workbook, 'Return Summary', [
    [returnLabel],
    ['Status', 'Draft figures for review before entering into ROS or filing software'],
    ['Business / legal name', getProfileName(profile)],
    ['Trading name', profile.tradingName],
    ['Tax reference', profile.taxReference],
    ['Company registration number', isLimitedCompany ? profile.companyRegistrationNumber : 'Not applicable'],
    ['Tax year / period', taxYear || 'All time'],
    ['Tax type', taxComputation?.taxLabel || (isLimitedCompany ? 'Corporation Tax' : 'Income Tax')]
  ], [34, 72]);

  appendSheet(workbook, 'ROS Input Draft', [
    ['Return field', 'Draft value', 'Notes'],
    ['Turnover / gross income', profitAndLoss?.totals?.income || 0, 'Agree to sales ledger.'],
    ['Allowable expenses', profitAndLoss?.totals?.expenses || 0, 'Review disallowable and private-use costs manually.'],
    ['Accounting profit after depreciation', taxComputation?.totals?.accountingProfit || 0, 'Profit per accounts.'],
    ['Add back book depreciation', taxComputation?.totals?.bookDepreciationAddBack || 0, 'Book depreciation is not normally deductible for tax.'],
    ['Capital allowances', taxComputation?.totals?.capitalAllowances || 0, 'Review eligibility and rates.'],
    ['Tax adjusted profit', taxComputation?.totals?.taxableProfit || 0, 'Draft taxable profit.'],
    [isLimitedCompany ? 'Corporation tax' : 'Estimated income tax', taxComputation?.totals?.totalTaxLiability || 0, 'Review against current rates and reliefs before filing.']
  ], [36, 18, 82]);

  appendSheet(workbook, 'Tax Computation', [
    ['Line', 'Amount'],
    ...(taxComputation?.rows || []).map((row) => [row.label, row.amount])
  ], [42, 18]);

  appendSheet(workbook, 'Capital Allowances', [
    ['Asset', 'Book Depreciation', 'Capital Allowance', 'Tax Treatment Notes'],
    ...(assetRegister?.rows || []).map((asset) => [
      asset.description || '',
      asset.annualDepreciation || 0,
      asset.capitalAllowanceForYear || 0,
      'Review capital allowance eligibility, private use, additions, disposals, and balancing adjustments.'
    ])
  ], [34, 18, 18, 82]);

  appendSheet(workbook, 'Review Points', [
    ['Area', 'Review point'],
    ['ROS', 'This is a preparation schedule only. Copy figures into ROS or tax software after review.'],
    ['Reliefs and credits', 'Add personal credits, reliefs, preliminary tax, losses, pension, payroll, and other tax-specific entries manually where relevant.'],
    ['Disallowable costs', 'Review entertainment, private use, fines, capital items, and other restricted expenses before filing.'],
    ['Corporation tax', 'For companies, confirm trading/non-trading split and applicable corporation tax rates.']
  ], [28, 100]);

  return workbook;
};

const nominalAccountOptions = Object.values(NOMINAL_ACCOUNTS);
const getAccountByCode = (accountCode) => nominalAccountOptions.find((account) => account.code === accountCode);

const getJournalTotals = (journal) => {
  const lines = Array.isArray(journal?.lines) ? journal.lines : [];
  const debit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
  const credit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
  return {
    debit,
    credit,
    difference: debit - credit,
    isBalanced: Math.abs(debit - credit) < 0.01
  };
};

function AccountantWorkspacePanel({
  transactions = [],
  assetTransactions = transactions,
  bankAccounts = [],
  journalEntries = [],
  taxYear,
  taxSummary = {},
  businessType = 'sole_trader',
  businessProfile = {},
  countryId = '',
  currencySymbol = 'EUR ',
  formatCurrency,
  getTransactionVatAmount,
  isCapitalExpense,
  onAssetRegisterChange,
  onBankAccountsChange,
  onJournalEntriesChange,
  onClose
}) {
  const [activeReport, setActiveReport] = useState('');
  const [selectedAccountsFramework, setSelectedAccountsFramework] = useState('frs105');
  const money = (value) => (
    typeof formatCurrency === 'function'
      ? formatCurrency(value)
      : formatMoney(value, currencySymbol)
  );

  const trialBalance = useMemo(() => buildTrialBalance(transactions, {
    taxYear,
    getTransactionVatAmount,
    isCapitalExpense,
    journalEntries
  }), [getTransactionVatAmount, isCapitalExpense, journalEntries, taxYear, transactions]);
  const balanceSheet = useMemo(() => (
    buildBalanceSheetFromTrialBalance(trialBalance, { bankAccounts })
  ), [bankAccounts, trialBalance]);
  const profitAndLoss = useMemo(() => (
    buildProfitAndLossFromTrialBalance(trialBalance)
  ), [trialBalance]);
  const assetRegister = useMemo(() => buildAssetRegister(assetTransactions, {
    taxYear,
    isCapitalExpense
  }), [assetTransactions, isCapitalExpense, taxYear]);
  const taxComputation = useMemo(() => buildTaxComputationSummary({
    profitAndLoss,
    assetRegister,
    taxSummary,
    taxYear,
    businessStructure: taxSummary.businessStructure || '',
    businessType,
    countryId
  }), [assetRegister, businessType, countryId, profitAndLoss, taxSummary, taxYear]);
  const comparativePeriod = useMemo(() => {
    const selectedYear = Number(taxYear) || null;
    if (!selectedYear) return null;
    const comparativeTaxYear = selectedYear - 1;
    const bundle = buildPeriodReportBundle({
      transactions,
      assetTransactions,
      bankAccounts,
      journalEntries,
      taxYear: comparativeTaxYear,
      taxSummary: {},
      businessType,
      countryId,
      getTransactionVatAmount,
      isCapitalExpense
    });
    return bundle.hasData ? bundle : null;
  }, [
    assetTransactions,
    bankAccounts,
    businessType,
    countryId,
    getTransactionVatAmount,
    isCapitalExpense,
    journalEntries,
    taxYear,
    transactions
  ]);

  const reportTransactions = useMemo(() => (
    getReportTransactions(transactions, taxYear)
  ), [taxYear, transactions]);
  const transactionCount = reportTransactions.length;

  const fixedAssetRows = trialBalance.rows.filter((row) => row.accountCode === '1500');
  const hasFixedAssets = fixedAssetRows.length > 0;
  const isBalanced = Math.abs(Number(trialBalance.difference) || 0) < 0.01;
  const balanceSheetBalances = Math.abs(Number(balanceSheet.totals.difference) || 0) < 0.01;
  const bankAccountRows = Array.isArray(bankAccounts) ? bankAccounts : [];
  const journalRows = Array.isArray(journalEntries) ? journalEntries : [];
  const totalBankBalance = bankAccountRows.reduce((sum, account) => sum + (Number(account.balance) || 0), 0);
  const unbalancedJournalCount = journalRows.filter((journal) => !getJournalTotals(journal).isBalanced).length;
  const summaryCardClass = 'rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 sm:p-4';
  const summaryLabelClass = 'text-[11px] font-medium uppercase tracking-wide text-gray-500 sm:text-xs';
  const summaryValueClass = 'mt-0.5 text-lg font-semibold leading-tight text-gray-900 sm:mt-1 sm:text-xl';

  const handleAddBankAccount = () => {
    const now = new Date().toISOString();
    const nextAccount = {
      id: `bank-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: 'Current Account',
      accountType: 'current',
      accountNumberHint: '',
      balance: 0,
      asOfDate: now.slice(0, 10),
      notes: '',
      createdAt: now,
      updatedAt: now
    };
    onBankAccountsChange?.([...bankAccountRows, nextAccount]);
    setActiveReport('banking');
  };

  const handleUpdateBankAccount = (accountId, updates) => {
    const now = new Date().toISOString();
    onBankAccountsChange?.(bankAccountRows.map((account) => (
      account.id === accountId
        ? { ...account, ...updates, updatedAt: now }
        : account
    )));
  };

  const handleDeleteBankAccount = (accountId) => {
    onBankAccountsChange?.(bankAccountRows.filter((account) => account.id !== accountId));
  };

  const handleAddJournalEntry = () => {
    const now = new Date().toISOString();
    const bankAccount = NOMINAL_ACCOUNTS.BANK;
    const capitalAccount = NOMINAL_ACCOUNTS.CAPITAL_INTRODUCED;
    const nextJournal = {
      id: `journal-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      date: now.slice(0, 10),
      reference: `JNL-${String(journalRows.length + 1).padStart(3, '0')}`,
      description: 'Manual journal',
      lines: [
        {
          id: `line-${Date.now()}-debit`,
          accountCode: bankAccount.code,
          accountName: bankAccount.name,
          accountType: bankAccount.type,
          debit: 0,
          credit: 0,
          memo: ''
        },
        {
          id: `line-${Date.now()}-credit`,
          accountCode: capitalAccount.code,
          accountName: capitalAccount.name,
          accountType: capitalAccount.type,
          debit: 0,
          credit: 0,
          memo: ''
        }
      ],
      createdAt: now,
      updatedAt: now
    };
    onJournalEntriesChange?.([...journalRows, nextJournal]);
    setActiveReport('journals');
  };

  const handleUpdateJournalEntry = (journalId, updates) => {
    const now = new Date().toISOString();
    onJournalEntriesChange?.(journalRows.map((journal) => (
      journal.id === journalId
        ? { ...journal, ...updates, updatedAt: now }
        : journal
    )));
  };

  const handleDeleteJournalEntry = (journalId) => {
    onJournalEntriesChange?.(journalRows.filter((journal) => journal.id !== journalId));
  };

  const handleUpdateJournalLine = (journalId, lineId, updates) => {
    const now = new Date().toISOString();
    onJournalEntriesChange?.(journalRows.map((journal) => {
      if (journal.id !== journalId) return journal;
      return {
        ...journal,
        updatedAt: now,
        lines: (Array.isArray(journal.lines) ? journal.lines : []).map((line) => (
          line.id === lineId ? { ...line, ...updates } : line
        ))
      };
    }));
  };

  const handleJournalLineAccountChange = (journalId, lineId, accountCode) => {
    const account = getAccountByCode(accountCode);
    if (!account) return;
    handleUpdateJournalLine(journalId, lineId, {
      accountCode: account.code,
      accountName: account.name,
      accountType: account.type
    });
  };

  const handleAddJournalLine = (journalId) => {
    const account = NOMINAL_ACCOUNTS.GENERAL_EXPENSES;
    const now = new Date().toISOString();
    onJournalEntriesChange?.(journalRows.map((journal) => {
      if (journal.id !== journalId) return journal;
      return {
        ...journal,
        updatedAt: now,
        lines: [
          ...(Array.isArray(journal.lines) ? journal.lines : []),
          {
            id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            accountCode: account.code,
            accountName: account.name,
            accountType: account.type,
            debit: 0,
            credit: 0,
            memo: ''
          }
        ]
      };
    }));
  };

  const handleDeleteJournalLine = (journalId, lineId) => {
    const now = new Date().toISOString();
    onJournalEntriesChange?.(journalRows.map((journal) => {
      if (journal.id !== journalId) return journal;
      const nextLines = (Array.isArray(journal.lines) ? journal.lines : []).filter((line) => line.id !== lineId);
      return { ...journal, updatedAt: now, lines: nextLines.length >= 2 ? nextLines : journal.lines };
    }));
  };

  const handleAssetFieldChange = (assetId, updates) => {
    onAssetRegisterChange?.(assetId, updates);
  };

  const handleExport = () => {
    if (activeReport === 'balanceSheet') {
      const csv = balanceSheetRowsToCsv(balanceSheet);
      downloadTextFile(`balance-sheet-${taxYear || 'all-time'}.csv`, csv);
      return;
    }

    if (activeReport === 'profitAndLoss') {
      const csv = profitAndLossRowsToCsv(profitAndLoss);
      downloadTextFile(`profit-and-loss-${taxYear || 'all-time'}.csv`, csv);
      return;
    }

    if (activeReport === 'taxComputation') {
      const csv = taxComputationRowsToCsv(taxComputation);
      downloadTextFile(`tax-computation-${taxYear || 'all-time'}.csv`, csv);
      return;
    }

    if (activeReport === 'banking') {
      const csv = bankAccountsToCsv(bankAccountRows);
      downloadTextFile(`bank-accounts-${taxYear || 'all-time'}.csv`, csv);
      return;
    }

    if (activeReport === 'journals') {
      const csv = journalEntriesToCsv(journalRows);
      downloadTextFile(`manual-journals-${taxYear || 'all-time'}.csv`, csv);
      return;
    }

    if (activeReport === 'assetRegister') {
      const csv = assetRegisterRowsToCsv(assetRegister.rows);
      downloadTextFile(`asset-register-${taxYear || 'all-time'}.csv`, csv);
      return;
    }

    const csv = trialBalanceRowsToCsv(trialBalance.rows);
    downloadTextFile(`trial-balance-${taxYear || 'all-time'}.csv`, csv);
  };

  const handleExportPack = () => {
    const workbook = buildAccountsWorkingPaperWorkbook({
      taxYear,
      trialBalance,
      profitAndLoss,
      balanceSheet,
      taxComputation,
      assetRegister,
      bankAccounts: bankAccountRows,
      journalEntries: journalRows,
      sourceTransactions: reportTransactions,
      getTransactionVatAmount
    });
    writeWorkbookFile(`accounts-working-paper-pack-${taxYear || 'all-time'}.xlsx`, workbook);
  };

  const handleExportAccountsDraft = async (templateType) => {
    const documentBodyXml = buildAbridgedAccountsDraftDocument({
      templateType,
      businessProfile: { ...businessProfile, businessType },
      taxYear,
      profitAndLoss,
      balanceSheet,
      taxComputation,
      assetRegister,
      bankAccounts: bankAccountRows,
      comparativePeriod
    });
    const templateSlug = templateType === 'frs102_1a' ? 'frs102-section-1a' : 'frs105';
    await writeDocxFile(`accounts-draft-${templateSlug}-${taxYear || 'all-time'}.docx`, documentBodyXml);
  };

  const handleExportTaxDraft = () => {
    const workbook = buildTaxReturnDraftWorkbook({
      businessProfile: { ...businessProfile, businessType },
      taxYear,
      businessType,
      profitAndLoss,
      taxComputation,
      assetRegister
    });
    const taxSlug = businessType === 'limited_company' ? 'corporation-tax' : 'income-tax';
    writeWorkbookFile(`${taxSlug}-return-draft-${taxYear || 'all-time'}.xlsx`, workbook);
  };

  const exportLabel = activeReport === 'balanceSheet'
    ? 'Balance Sheet'
    : activeReport === 'profitAndLoss'
      ? 'Profit & Loss'
      : activeReport === 'taxComputation'
        ? 'Tax Computation'
        : activeReport === 'banking'
          ? 'Bank Accounts'
          : activeReport === 'journals'
            ? 'Manual Journals'
            : activeReport === 'assetRegister'
              ? 'Asset Register'
              : activeReport === 'trialBalance'
                ? 'Trial Balance'
                : 'Report';
  const exportDisabled = !activeReport
    ? true
    : activeReport === 'banking'
    ? bankAccountRows.length === 0
    : activeReport === 'profitAndLoss'
      ? profitAndLoss.sections.income.length === 0 && profitAndLoss.sections.expenses.length === 0
      : activeReport === 'taxComputation'
        ? taxComputation.rows.length === 0
        : activeReport === 'journals'
          ? journalRows.length === 0
          : activeReport === 'assetRegister'
            ? assetRegister.rows.length === 0
            : trialBalance.rows.length === 0;

  const renderBalanceSheetSection = (title, rows, total) => (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <span className="text-sm font-semibold text-gray-900">{money(total)}</span>
      </div>
      <table className="min-w-full divide-y divide-gray-100 text-sm">
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.length === 0 ? (
            <tr>
              <td className="px-4 py-3 text-gray-500" colSpan="3">No balance</td>
            </tr>
          ) : rows.map((row) => (
            <tr key={`${title}-${row.accountCode}`}>
              <td className="px-4 py-3 font-mono text-gray-700">{row.accountCode}</td>
              <td className="px-4 py-3 text-gray-900">{row.accountName}</td>
              <td className="px-4 py-3 text-right text-gray-900">{money(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderProfitAndLossSection = (title, rows, total) => (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <span className="text-sm font-semibold text-gray-900">{money(total)}</span>
      </div>
      <table className="min-w-full divide-y divide-gray-100 text-sm">
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.length === 0 ? (
            <tr>
              <td className="px-4 py-3 text-gray-500" colSpan="3">No postings</td>
            </tr>
          ) : rows.map((row) => (
            <tr key={`${title}-${row.accountCode}`}>
              <td className="px-4 py-3 font-mono text-gray-700">{row.accountCode}</td>
              <td className="px-4 py-3 text-gray-900">{row.accountName}</td>
              <td className="px-4 py-3 text-right text-gray-900">{money(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div id="accountant-workspace-section" className="w-full">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-700" />
              <h3 className="text-lg font-medium text-gray-900">Accountant Workspace</h3>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Accounting-grade reports for review, export, and future compliance packs.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportPack}
              disabled={trialBalance.rows.length === 0 && assetRegister.rows.length === 0 && bankAccountRows.length === 0 && journalRows.length === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-[color:var(--color-accent)] px-4 py-2 text-sm font-semibold text-[color:var(--color-text)] transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={16} />
              Export Pack
            </button>
            <select
              value={selectedAccountsFramework}
              onChange={(event) => setSelectedAccountsFramework(event.target.value)}
              className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700"
              title="Accounts framework"
            >
              <option value="frs105">FRS 105</option>
              <option value="frs102_1a">FRS 102 Section 1A</option>
            </select>
            <button
              type="button"
              onClick={() => handleExportAccountsDraft(selectedAccountsFramework)}
              disabled={profitAndLoss.sections.income.length === 0 && balanceSheet.totals.assets === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={16} />
              Accounts Word Draft
            </button>
            <button
              type="button"
              onClick={handleExportTaxDraft}
              disabled={taxComputation.rows.length === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={16} />
              Tax Draft
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exportDisabled}
              className="inline-flex items-center gap-2 rounded-lg bg-[color:var(--color-info)] px-4 py-2 text-sm font-semibold text-[color:var(--color-text)] transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={16} />
              Export {exportLabel}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition"
            >
              <X size={16} />
              Close
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex flex-wrap rounded-lg border border-gray-200 bg-gray-50 p-1">
            {[
              { id: 'trialBalance', label: 'Trial Balance' },
              { id: 'profitAndLoss', label: 'Profit & Loss' },
              { id: 'balanceSheet', label: 'Balance Sheet' },
              { id: 'taxComputation', label: 'Tax Computation' },
              { id: 'assetRegister', label: 'Asset Register' },
              { id: 'journals', label: 'Journals' },
              { id: 'banking', label: 'Banking' }
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setActiveReport((current) => (current === option.id ? '' : option.id))}
                aria-expanded={activeReport === option.id}
                className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
                  activeReport === option.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {!activeReport ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-5 text-center text-sm text-gray-500">
              Choose a report card above to open it.
            </div>
          ) : (
            <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
            <div className={summaryCardClass}>
              <div className={summaryLabelClass}>Period</div>
              <div className={summaryValueClass}>{taxYear || 'All time'}</div>
            </div>
            <div className={summaryCardClass}>
              <div className={summaryLabelClass}>Transactions</div>
              <div className={summaryValueClass}>{transactionCount}</div>
            </div>
            <div className={summaryCardClass}>
              <div className={summaryLabelClass}>Debits</div>
              <div className={summaryValueClass}>{money(trialBalance.totalDebits)}</div>
            </div>
            <div className={summaryCardClass}>
              <div className={summaryLabelClass}>Bank Balance</div>
              <div className={summaryValueClass}>{money(totalBankBalance)}</div>
            </div>
          </div>

          {unbalancedJournalCount > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {unbalancedJournalCount} manual journal{unbalancedJournalCount === 1 ? '' : 's'} are unbalanced and excluded from reports until debits equal credits.
            </div>
          )}

          <div className={`rounded-lg border px-4 py-3 text-sm ${
            isBalanced ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'
          }`}>
            {isBalanced
              ? 'Trial balance is balanced from the current transaction postings.'
              : `Trial balance difference: ${money(trialBalance.difference)}. Review transaction mappings before using this for accounts production.`}
          </div>

          {!hasFixedAssets && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              No fixed asset account balance appears for this period. Capital purchases will appear here once expense categories such as equipment, furniture, electronics, salon tools, or vehicle are used.
            </div>
          )}

          {activeReport === 'journals' ? (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Manual Journals and Opening Adjustments</h4>
                  <p className="mt-1 text-xs text-gray-600">
                    Add opening balances, accruals, debtors, creditors, corrections, and year-end adjustments.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddJournalEntry}
                  className="inline-flex items-center justify-center rounded-lg bg-[color:var(--color-accent)] px-4 py-2 text-sm font-semibold text-[color:var(--color-text)] transition hover:brightness-95"
                >
                  Add Journal
                </button>
              </div>

              {journalRows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                  No manual journals added yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {journalRows.map((journal) => {
                    const totals = getJournalTotals(journal);
                    return (
                      <div key={journal.id} className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[140px_150px_minmax(0,1fr)_auto]">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">Date</label>
                            <input
                              type="date"
                              value={journal.date || ''}
                              onChange={(event) => handleUpdateJournalEntry(journal.id, { date: event.target.value })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">Reference</label>
                            <input
                              type="text"
                              value={journal.reference || ''}
                              onChange={(event) => handleUpdateJournalEntry(journal.id, { reference: event.target.value })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">Description</label>
                            <input
                              type="text"
                              value={journal.description || ''}
                              onChange={(event) => handleUpdateJournalEntry(journal.id, { description: event.target.value })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => handleDeleteJournalEntry(journal.id)}
                              className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">Account</th>
                                <th className="px-3 py-2 text-right font-semibold text-gray-700">Debit</th>
                                <th className="px-3 py-2 text-right font-semibold text-gray-700">Credit</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">Memo</th>
                                <th className="px-3 py-2 text-right font-semibold text-gray-700">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {(Array.isArray(journal.lines) ? journal.lines : []).map((line) => (
                                <tr key={line.id}>
                                  <td className="px-3 py-2">
                                    <select
                                      value={line.accountCode || ''}
                                      onChange={(event) => handleJournalLineAccountChange(journal.id, line.id, event.target.value)}
                                      className="min-w-[220px] rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    >
                                      {nominalAccountOptions.map((account) => (
                                        <option key={account.code} value={account.code}>
                                          {account.code} - {account.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={line.debit ?? 0}
                                      onChange={(event) => handleUpdateJournalLine(journal.id, line.id, { debit: event.target.value === '' ? '' : Number(event.target.value), credit: 0 })}
                                      className="w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={line.credit ?? 0}
                                      onChange={(event) => handleUpdateJournalLine(journal.id, line.id, { credit: event.target.value === '' ? '' : Number(event.target.value), debit: 0 })}
                                      className="w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="text"
                                      value={line.memo || ''}
                                      onChange={(event) => handleUpdateJournalLine(journal.id, line.id, { memo: event.target.value })}
                                      className="min-w-[180px] rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteJournalLine(journal.id, line.id)}
                                      className="text-sm font-medium text-red-700 hover:text-red-900"
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                              <tr>
                                <td className="px-3 py-2 font-semibold text-gray-900">
                                  <button
                                    type="button"
                                    onClick={() => handleAddJournalLine(journal.id)}
                                    className="text-sm font-semibold text-blue-700 hover:text-blue-900"
                                  >
                                    Add line
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-right font-semibold text-gray-900">{money(totals.debit)}</td>
                                <td className="px-3 py-2 text-right font-semibold text-gray-900">{money(totals.credit)}</td>
                                <td className={`px-3 py-2 font-semibold ${totals.isBalanced ? 'text-green-700' : 'text-amber-800'}`} colSpan="2">
                                  {totals.isBalanced ? 'Balanced' : `Difference ${money(totals.difference)}`}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : activeReport === 'taxComputation' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3">
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Tax Type</div>
                  <div className={summaryValueClass}>{taxComputation.taxLabel}</div>
                </div>
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Taxable Profit</div>
                  <div className={summaryValueClass}>{money(taxComputation.totals.taxableProfit)}</div>
                </div>
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Estimated Tax</div>
                  <div className={summaryValueClass}>{money(taxComputation.totals.totalTaxLiability)}</div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <div className="bg-gray-50 px-4 py-3">
                  <h4 className="text-sm font-semibold text-gray-900">Tax Computation Schedule</h4>
                  <p className="mt-1 text-xs text-gray-600">
                    Review figures before copying them into Revenue Online Service or accounts-production software.
                  </p>
                </div>
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {taxComputation.rows.map((row) => (
                      <tr key={row.label}>
                        <td className="px-4 py-3 text-gray-900">{row.label}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">{money(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {taxComputation.capitalAllowanceRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Asset</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Book Depn</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Capital Allowance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {taxComputation.capitalAllowanceRows
                        .filter((row) => (Number(row.bookDepreciation) || 0) !== 0 || (Number(row.capitalAllowance) || 0) !== 0)
                        .map((row) => (
                          <tr key={row.id || row.description}>
                            <td className="px-4 py-3 text-gray-900">{row.description || row.id}</td>
                            <td className="px-4 py-3 text-right text-gray-900">{money(row.bookDepreciation || 0)}</td>
                            <td className="px-4 py-3 text-right text-gray-900">{money(row.capitalAllowance || 0)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : activeReport === 'profitAndLoss' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3">
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Income</div>
                  <div className={summaryValueClass}>{money(profitAndLoss.totals.income)}</div>
                </div>
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Expenses</div>
                  <div className={summaryValueClass}>{money(profitAndLoss.totals.expenses)}</div>
                </div>
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Net Profit</div>
                  <div className={summaryValueClass}>{money(profitAndLoss.totals.netProfit)}</div>
                </div>
              </div>

              {renderProfitAndLossSection(
                'Income',
                profitAndLoss.sections.income,
                profitAndLoss.totals.income
              )}
              {renderProfitAndLossSection(
                'Expenses',
                profitAndLoss.sections.expenses,
                profitAndLoss.totals.expenses
              )}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                <div className="flex justify-between text-sm font-semibold text-gray-900">
                  <span>Net Profit</span>
                  <span>{money(profitAndLoss.totals.netProfit)}</span>
                </div>
              </div>
            </div>
          ) : activeReport === 'assetRegister' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Assets</div>
                  <div className={summaryValueClass}>{assetRegister.rows.length}</div>
                </div>
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Cost</div>
                  <div className={summaryValueClass}>{money(assetRegister.totals.cost)}</div>
                </div>
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>NBV</div>
                  <div className={summaryValueClass}>{money(assetRegister.totals.netBookValue)}</div>
                </div>
                <div className={summaryCardClass}>
                  <div className={summaryLabelClass}>Capital Allowance</div>
                  <div className={summaryValueClass}>{money(assetRegister.totals.capitalAllowanceForYear)}</div>
                </div>
              </div>

              <div className="space-y-3 lg:hidden">
                {assetRegister.rows.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-center text-sm text-gray-500">
                    No capital assets found yet.
                  </div>
                ) : assetRegister.rows.map((asset) => (
                  <div key={asset.id} className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="grid grid-cols-1 gap-2">
                      <label className="text-xs font-medium text-gray-600">
                        Asset
                        <input
                          type="text"
                          value={asset.description || ''}
                          onChange={(event) => handleAssetFieldChange(asset.id, { description: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                        />
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-xs font-medium text-gray-600">
                          Purchased
                          <input
                            type="date"
                            value={asset.purchaseDate || ''}
                            onChange={(event) => handleAssetFieldChange(asset.id, { date: event.target.value })}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                          />
                        </label>
                        <label className="text-xs font-medium text-gray-600">
                          Status
                          <select
                            value={asset.status || 'active'}
                            onChange={(event) => handleAssetFieldChange(asset.id, { assetStatus: event.target.value })}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                          >
                            <option value="active">Active</option>
                            <option value="sold">Sold</option>
                            <option value="written-off">Written off</option>
                          </select>
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-xs font-medium text-gray-600">
                          Cost
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={asset.cost}
                            onChange={(event) => handleAssetFieldChange(asset.id, { amount: event.target.value === '' ? '' : Number(event.target.value) })}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900"
                          />
                        </label>
                        <label className="text-xs font-medium text-gray-600">
                          Depn years
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={asset.depreciationYears}
                            onChange={(event) => handleAssetFieldChange(asset.id, { depreciationYears: event.target.value === '' ? '' : Number(event.target.value) })}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900"
                          />
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-xs font-medium text-gray-600">
                          Disposal date
                          <input
                            type="date"
                            value={asset.disposalDate || ''}
                            onChange={(event) => handleAssetFieldChange(asset.id, { disposalDate: event.target.value })}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                          />
                        </label>
                        <label className="text-xs font-medium text-gray-600">
                          Proceeds
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={asset.disposalAmount || 0}
                            onChange={(event) => handleAssetFieldChange(asset.id, { disposalAmount: event.target.value === '' ? '' : Number(event.target.value) })}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900"
                          />
                        </label>
                      </div>
                      <div className="grid grid-cols-3 gap-2 rounded-md bg-gray-50 p-2 text-xs text-gray-700">
                        <div>
                          <div className="font-medium text-gray-500">NBV</div>
                          <div className="font-semibold text-gray-900">{money(asset.netBookValue)}</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-500">CA</div>
                          <div className="font-semibold text-gray-900">{money(asset.capitalAllowanceForYear)}</div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-500">Gain/Loss</div>
                          <div className="font-semibold text-gray-900">{asset.disposalDate ? money(asset.gainOrLossOnDisposal) : '-'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden overflow-x-auto rounded-lg border border-gray-200 lg:block">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Asset</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Purchased</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Cost</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Depn yrs</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Accum Depn</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">NBV</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">CA</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Disposal</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Gain / Loss</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {assetRegister.rows.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                          No capital assets found yet.
                        </td>
                      </tr>
                    ) : assetRegister.rows.map((asset) => (
                      <tr key={asset.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">
                          <input
                            type="text"
                            value={asset.description || ''}
                            onChange={(event) => handleAssetFieldChange(asset.id, { description: event.target.value })}
                            className="w-48 rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                          />
                          <div className="mt-1 text-xs text-gray-500">{asset.category}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <select
                            value={asset.status || 'active'}
                            onChange={(event) => handleAssetFieldChange(asset.id, { assetStatus: event.target.value })}
                            className="w-32 rounded-lg border border-gray-300 px-2 py-1.5 text-sm capitalize text-gray-900"
                          >
                            <option value="active">Active</option>
                            <option value="sold">Sold</option>
                            <option value="written-off">Written off</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <input
                            type="date"
                            value={asset.purchaseDate || ''}
                            onChange={(event) => handleAssetFieldChange(asset.id, { date: event.target.value })}
                            className="w-36 rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={asset.cost}
                            onChange={(event) => handleAssetFieldChange(asset.id, { amount: event.target.value === '' ? '' : Number(event.target.value) })}
                            className="w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900"
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={asset.depreciationYears}
                            onChange={(event) => handleAssetFieldChange(asset.id, { depreciationYears: event.target.value === '' ? '' : Number(event.target.value) })}
                            className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900"
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">{money(asset.accumulatedDepreciation)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{money(asset.netBookValue)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{money(asset.capitalAllowanceForYear)}</td>
                        <td className="px-4 py-3 text-gray-900">
                          <div className="flex gap-2">
                            <input
                              type="date"
                              value={asset.disposalDate || ''}
                              onChange={(event) => handleAssetFieldChange(asset.id, { disposalDate: event.target.value })}
                              className="w-36 rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
                            />
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={asset.disposalAmount || 0}
                              onChange={(event) => handleAssetFieldChange(asset.id, { disposalAmount: event.target.value === '' ? '' : Number(event.target.value) })}
                              className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-right text-sm text-gray-900"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">{asset.disposalDate ? money(asset.gainOrLossOnDisposal) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeReport === 'banking' ? (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Bank Accounts and Cash Balances</h4>
                  <p className="mt-1 text-xs text-gray-600">
                    Add cash, current, savings, card, or reserve balances for the balance sheet and year-end packs.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddBankAccount}
                  className="inline-flex items-center justify-center rounded-lg bg-[color:var(--color-accent)] px-4 py-2 text-sm font-semibold text-[color:var(--color-text)] transition hover:brightness-95"
                >
                  Add Account
                </button>
              </div>

              {bankAccountRows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                  No bank accounts added yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {bankAccountRows.map((account) => (
                    <div key={account.id} className="rounded-lg border border-gray-200 bg-white p-4">
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.4fr)_160px_150px_150px_auto]">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Account Name</label>
                          <input
                            type="text"
                            value={account.name || ''}
                            onChange={(event) => handleUpdateBankAccount(account.id, { name: event.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Current Account"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Type</label>
                          <select
                            value={account.accountType || 'current'}
                            onChange={(event) => handleUpdateBankAccount(account.id, { accountType: event.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          >
                            <option value="cash">Cash</option>
                            <option value="current">Current Account</option>
                            <option value="savings">Savings</option>
                            <option value="card">Card / Merchant</option>
                            <option value="reserve">Reserve</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Balance</label>
                          <input
                            type="number"
                            step="0.01"
                            value={account.balance ?? 0}
                            onChange={(event) => handleUpdateBankAccount(account.id, { balance: event.target.value === '' ? '' : Number(event.target.value) })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">As Of</label>
                          <input
                            type="date"
                            value={account.asOfDate || ''}
                            onChange={(event) => handleUpdateBankAccount(account.id, { asOfDate: event.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => handleDeleteBankAccount(account.id)}
                            className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Account Hint</label>
                          <input
                            type="text"
                            value={account.accountNumberHint || ''}
                            onChange={(event) => handleUpdateBankAccount(account.id, { accountNumberHint: event.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Last 4 digits or nickname"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
                          <input
                            type="text"
                            value={account.notes || ''}
                            onChange={(event) => handleUpdateBankAccount(account.id, { notes: event.target.value })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Optional review note"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeReport === 'balanceSheet' ? (
            <div className="space-y-4">
              <div className={`rounded-lg border px-4 py-3 text-sm ${
                balanceSheetBalances ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'
              }`}>
                {balanceSheetBalances
                  ? 'Balance sheet balances from the current trial balance.'
                  : `Balance sheet difference: ${money(balanceSheet.totals.difference)}. Add opening balances or review postings before using this for accounts production.`}
              </div>

              {renderBalanceSheetSection(
                'Non-Current Assets',
                balanceSheet.sections.nonCurrentAssets,
                balanceSheet.totals.nonCurrentAssets
              )}
              {renderBalanceSheetSection(
                'Current Assets',
                balanceSheet.sections.currentAssets,
                balanceSheet.totals.currentAssets
              )}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                <div className="flex justify-between text-sm font-semibold text-gray-900">
                  <span>Total Assets</span>
                  <span>{money(balanceSheet.totals.assets)}</span>
                </div>
              </div>
              {renderBalanceSheetSection(
                'Current Liabilities',
                balanceSheet.sections.currentLiabilities,
                balanceSheet.totals.currentLiabilities
              )}
              {renderBalanceSheetSection(
                'Non-Current Liabilities',
                balanceSheet.sections.nonCurrentLiabilities,
                balanceSheet.totals.nonCurrentLiabilities
              )}
              {renderBalanceSheetSection(
                'Capital and Reserves',
                balanceSheet.sections.equity,
                balanceSheet.totals.equity
              )}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                <div className="flex justify-between text-sm font-semibold text-gray-900">
                  <span>Total Liabilities and Capital</span>
                  <span>{money(balanceSheet.totals.liabilitiesAndEquity)}</span>
                </div>
              </div>
            </div>
          ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Code</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Account</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Debit</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Credit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {trialBalance.rows.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      No transactions found for this period.
                    </td>
                  </tr>
                ) : trialBalance.rows.map((row) => (
                  <tr key={row.accountCode} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-700">{row.accountCode}</td>
                    <td className="px-4 py-3 text-gray-900">{row.accountName}</td>
                    <td className="px-4 py-3 capitalize text-gray-600">{row.accountType}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{row.debit ? money(row.debit) : '-'}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{row.credit ? money(row.credit) : '-'}</td>
                  </tr>
                ))}
              </tbody>
              {trialBalance.rows.length > 0 && (
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-4 py-3 font-semibold text-gray-900" colSpan="3">Total</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{money(trialBalance.totalDebits)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{money(trialBalance.totalCredits)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
            Current version combines saved transactions, manual journals, bank balances, and capital asset schedules. Accounts-production mappings and document templates are the next layers.
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountantWorkspacePanel;
